/**
 * @sazeo/schemas — Invoicing
 *
 * Zod schemas for the Invoicing addon (migration 0012 tables). Wire shapes
 * shared between the tenant-app SDK and the Edge Functions.
 *
 * Spec: specs/CODEX_PHASE4_WAVE2_CRM_INVOICING.md §2.3
 */

import { z } from 'zod';
import { TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Row shapes
// =============================================================================

export const InvCustomerAddressSchema = z
  .object({
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    region: z.string().optional(),
    postal_code: z.string().optional(),
    country: z.string().optional(),
  })
  .partial()
  .catchall(z.unknown());
export type InvCustomerAddress = z.infer<typeof InvCustomerAddressSchema>;

export const InvCustomerSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  name: z.string().min(1),
  email: z.string().email().nullable(),
  address: InvCustomerAddressSchema,
  tax_id: z.string().nullable(),
  created_by: UuidSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type InvCustomer = z.infer<typeof InvCustomerSchema>;

export const InvInvoiceStatusSchema = z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled']);
export type InvInvoiceStatus = z.infer<typeof InvInvoiceStatusSchema>;

export const InvInvoiceSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  customer_id: UuidSchema,
  number: z.string().min(1),
  issue_date: z.string().date(),
  due_date: z.string().date().nullable(),
  currency: z.string().min(3).max(8),
  subtotal: z.number(),
  tax_total: z.number(),
  total: z.number(),
  status: InvInvoiceStatusSchema,
  paid_at: TimestampSchema.nullable(),
  notes: z.string().nullable(),
  created_by: UuidSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type InvInvoice = z.infer<typeof InvInvoiceSchema>;

export const InvInvoiceLineSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  invoice_id: UuidSchema,
  position: z.number().int().nonnegative(),
  description: z.string().min(1),
  quantity: z.number().nonnegative(),
  unit_price: z.number().nonnegative(),
  tax_percent: z.number().min(0).max(100),
  line_total: z.number(),
});
export type InvInvoiceLine = z.infer<typeof InvInvoiceLineSchema>;

// =============================================================================
// Input shapes
// =============================================================================

export const InvCustomerCreateInputSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email().nullable().optional(),
  address: InvCustomerAddressSchema.optional(),
  tax_id: z.string().max(80).nullable().optional(),
});
export type InvCustomerCreateInput = z.infer<typeof InvCustomerCreateInputSchema>;

export const InvCustomerUpdateInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    email: z.string().email().nullable(),
    address: InvCustomerAddressSchema,
    tax_id: z.string().max(80).nullable(),
  })
  .partial();
export type InvCustomerUpdateInput = z.infer<typeof InvCustomerUpdateInputSchema>;

export const InvInvoiceCreateInputSchema = z.object({
  customer_id: UuidSchema,
  number: z.string().min(1).max(60),
  issue_date: z.string().date().optional(),
  due_date: z.string().date().nullable().optional(),
  currency: z.string().min(3).max(8).optional(),
  notes: z.string().max(4000).nullable().optional(),
});
export type InvInvoiceCreateInput = z.infer<typeof InvInvoiceCreateInputSchema>;

export const InvInvoiceUpdateInputSchema = z
  .object({
    customer_id: UuidSchema,
    number: z.string().min(1).max(60),
    issue_date: z.string().date(),
    due_date: z.string().date().nullable(),
    currency: z.string().min(3).max(8),
    status: InvInvoiceStatusSchema,
    notes: z.string().max(4000).nullable(),
  })
  .partial();
export type InvInvoiceUpdateInput = z.infer<typeof InvInvoiceUpdateInputSchema>;

export const InvInvoiceLineCreateInputSchema = z.object({
  position: z.number().int().nonnegative(),
  description: z.string().min(1).max(400),
  quantity: z.number().nonnegative(),
  unit_price: z.number().nonnegative(),
  tax_percent: z.number().min(0).max(100).optional(),
});
export type InvInvoiceLineCreateInput = z.infer<typeof InvInvoiceLineCreateInputSchema>;

export const InvInvoiceLineUpdateInputSchema = z
  .object({
    position: z.number().int().nonnegative(),
    description: z.string().min(1).max(400),
    quantity: z.number().nonnegative(),
    unit_price: z.number().nonnegative(),
    tax_percent: z.number().min(0).max(100),
  })
  .partial();
export type InvInvoiceLineUpdateInput = z.infer<typeof InvInvoiceLineUpdateInputSchema>;

// =============================================================================
// Filter shapes
// =============================================================================

export const InvInvoiceFilterSchema = z
  .object({
    status: InvInvoiceStatusSchema.optional(),
    customer_id: UuidSchema.optional(),
    from_issue_date: z.string().date().optional(),
    to_issue_date: z.string().date().optional(),
    search: z.string().optional(),
    limit: z.number().int().positive().max(200).optional(),
  })
  .partial();
export type InvInvoiceFilter = z.infer<typeof InvInvoiceFilterSchema>;

export const InvCustomerFilterSchema = z
  .object({
    search: z.string().optional(),
    limit: z.number().int().positive().max(200).optional(),
  })
  .partial();
export type InvCustomerFilter = z.infer<typeof InvCustomerFilterSchema>;

// =============================================================================
// Suppliers (migration 0042)
// =============================================================================

export const InvSupplierSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  name: z.string().min(1),
  contact_name: z.string().nullable(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  tax_id: z.string().nullable(),
  currency: z.string().min(3).max(8),
  notes: z.string().nullable(),
  is_active: z.boolean(),
  created_by: UuidSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type InvSupplier = z.infer<typeof InvSupplierSchema>;

export const InvSupplierCreateInputSchema = z.object({
  name: z.string().min(1).max(200),
  contact_name: z.string().max(200).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  tax_id: z.string().max(80).nullable().optional(),
  currency: z.string().min(3).max(8).default('GEL'),
  notes: z.string().max(4000).nullable().optional(),
  is_active: z.boolean().default(true),
});
export type InvSupplierCreateInput = z.infer<typeof InvSupplierCreateInputSchema>;

export const InvSupplierUpdateInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    contact_name: z.string().max(200).nullable(),
    email: z.string().email().nullable(),
    phone: z.string().max(50).nullable(),
    address: z.string().max(500).nullable(),
    tax_id: z.string().max(80).nullable(),
    currency: z.string().min(3).max(8),
    notes: z.string().max(4000).nullable(),
    is_active: z.boolean(),
  })
  .partial();
export type InvSupplierUpdateInput = z.infer<typeof InvSupplierUpdateInputSchema>;

export const InvSupplierFilterSchema = z
  .object({
    search: z.string().optional(),
    is_active: z.boolean().optional(),
    limit: z.number().int().positive().max(200).optional(),
  })
  .partial();
export type InvSupplierFilter = z.infer<typeof InvSupplierFilterSchema>;
